import React, { useState } from "react";
import { View, StyleSheet, Platform, Text } from "react-native";
import CrossPlatformDropdown from "../../components/CrossPlatformDropdown";
import LhButton from "../../components/LhButton";
import LHOverlay from "../../components/LHOverlay";
import { DropdownItem } from "../../types/observation";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../../constants/fonts";
import { useTheme } from "../../theme";

interface Props {
  isVisible: boolean;
  onClose: () => void;
  onSave: () => void;
  selectedZone: DropdownItem | null;
  selectedCategory: DropdownItem | null;
  setSelectedZone: (item: DropdownItem | null) => void;
  setSelectedCategory: (item: DropdownItem | null) => void;
  zoneOptions: DropdownItem[];
  categoryOptions: DropdownItem[];
  handleSaveFilters: () => void;
}

const SparcWalkBegin: React.FC<Props> = ({
  isVisible,
  onClose,
  selectedZone,
  selectedCategory,
  setSelectedZone,
  setSelectedCategory,
  zoneOptions,
  categoryOptions,
  handleSaveFilters,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [errors, setErrors] = useState({ zone: false, category: false });

  const handleSave = () => {
    const zoneMissing = !selectedZone;
    const categoryMissing = !selectedCategory;

    if (zoneMissing || categoryMissing) {
      setErrors({ zone: zoneMissing, category: categoryMissing });
    } else {
      setErrors({ zone: false, category: false });
      handleSaveFilters(); // ✅ Trigger the navigation
    }
  };

  return (
    <LHOverlay
      visible={isVisible}
      onDismiss={onClose}
      title="SPARC Walk Selection"
      size="medium"
    >
      <View style={styles.filterOverlayContainer}>
        {/* Zone Dropdown with custom label */}
        <View style={styles.responsiveDropdown}>
          <View style={styles.labelRow}>
            <View style={styles.labelLeft}>
              <Text
                style={[styles.fieldLabel, { color: colors.text.primary }]}
              >
                Zone
              </Text>
              <MaterialIcons
                name="info-outline"
                size={16}
                color={isDark ? colors.text.secondary : "#6c757d"}
                style={styles.infoIcon}
              />
            </View>
            <Text
              style={[
                styles.requiredText,
                { color: isDark ? colors.text.secondary : colors.text.primary },
              ]}
            >
              *Required
            </Text>
          </View>
          <CrossPlatformDropdown
            testID="zone-dropdown"
            childList={zoneOptions}
            label=""
            placeholder="Select Zone of SPARC Walk"
            selection="single"
            selectedValue={selectedZone}
            setSelectedValue={(value: any) => {
              const selectedItem = Array.isArray(value) ? value[0] : value;
              setSelectedZone(selectedItem);
              setErrors((prev) => ({ ...prev, zone: false }));
            }}
            feedbackType={errors.zone ? "error" : "info"}
            helperText={errors.zone ? "This field is required" : ""}
          />
        </View>

        {/* Category Dropdown with custom label */}
        <View style={styles.responsiveDropdown}>
          <View style={styles.labelRow}>
            <View style={styles.labelLeft}>
              <Text
                style={[styles.fieldLabel, { color: colors.text.primary }]}
              >
                Category
              </Text>
              <MaterialIcons
                name="info-outline"
                size={16}
                color={isDark ? colors.text.secondary : "#6c757d"}
                style={styles.infoIcon}
              />
            </View>
            <Text
              style={[
                styles.requiredText,
                { color: isDark ? colors.text.secondary : colors.text.primary },
              ]}
            >
              *Required
            </Text>
          </View>
          <CrossPlatformDropdown
            testID="category-dropdown"
            childList={categoryOptions}
            label=""
            placeholder="Select Category"
            selection="single"
            selectedValue={selectedCategory}
            setSelectedValue={(value: any) => {
              const selectedItem = Array.isArray(value) ? value[0] : value;
              setSelectedCategory(selectedItem);
              setErrors((prev) => ({ ...prev, category: false }));
            }}
            feedbackType={errors.category ? "error" : "info"}
            helperText={errors.category ? "This field is required" : ""}
            disableSorting={true}
          />
        </View>

        <View style={styles.filterOverlayButtons}>
          <LhButton
            testID="cancel-button"
            text="Cancel"
            variant="secondary"
            size="small"
            onPress={onClose}
            isLoading={false}
            disabled={false}
            style={{ ...styles.filterOverlayButton, ...styles.cancelButton }}
          />
          <LhButton
            testID="proceed-button"
            text="Save"
            variant="primary"
            size="small"
            onPress={handleSave}
            isLoading={false}
            disabled={false}
            style={styles.filterOverlayButton}
          />
        </View>
      </View>
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  filterOverlayContainer: {
    padding: Platform.select({ web: 16, ios: 0 }),
  },
  responsiveDropdown: {
    width: "100%",
    marginBottom: 12,
  },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  labelLeft: {
    flexDirection: "row",
    alignItems: "center",
  },
  fieldLabel: {
    fontSize: 14,
    fontWeight: "600",
    color: "#FFFFFF",
    marginRight: 6,
    fontFamily: FontFamily.regular,
  },
  infoIcon: {
    marginTop: 2,
  },
  requiredText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontFamily: FontFamily.regular,
  },
  filterOverlayButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    gap: 10,
  },
  filterOverlayButton: {
    width: Platform.OS === "web" ? "15%" : "40%",
    fontSize: 16,
    fontFamily: FontFamily.regular,
  },
  cancelButton: {
    borderWidth: 0,
    backgroundColor: "transparent",
  },
});

export default SparcWalkBegin;
